import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { api } from "../../api";

// ─── Context ─────────────────────────────────────────────────────────────────
const AudioPlayerContext = createContext(null);

export function useAudioPlayer() {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx)
    throw new Error("useAudioPlayer must be used within AudioPlayerProvider");
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function AudioPlayerProvider({ children }) {
  const [activeLecture, setActiveLecture] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const [playbackRate, setPlaybackRateState] = useState(1);

  const audioRef = useRef(null);

  const progress = duration > 0 ? currentTime / duration : 0;

  // ─── Load & auto-play a lecture ───────────────────────────────────────────
  const playLecture = useCallback(
    (lecture) => {
      // If same lecture is already active, just toggle play/pause
      if (activeLecture?._id === lecture._id) {
        togglePlay();
        return;
      }

      // Clean up previous audio
      if (audioRef.current) {
        const old = audioRef.current;
        old.onerror = null;
        old.oncanplay = null;
        old.ontimeupdate = null;
        old.onloadedmetadata = null;
        old.onended = null;
        old.pause();
        old.removeAttribute("src");
        old.load();
      }

      setActiveLecture(lecture);
      setCurrentTime(0);
      setDuration(lecture.duration || 0);
      setLoading(true);
      setStreamError(false);
      setPlaying(false);

      let cancelled = false;

      (async () => {
        try {
          const { data } = await api.get(
            `/audio-lectures/${lecture._id}/stream`,
          );
          if (cancelled) return;
          if (!data?.success || !data?.url)
            throw new Error("No stream URL returned");

          const audio = new Audio(data.url);
          audioRef.current = audio;
          audio.volume = muted ? 0 : volume;
          audio.playbackRate = playbackRate;

          audio.onloadedmetadata = () => {
            if (audio.duration && isFinite(audio.duration))
              setDuration(audio.duration);
          };
          audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
          audio.onended = () => {
            setPlaying(false);
            setCurrentTime(0);
          };
          audio.oncanplay = () => {
            if (cancelled) return;
            setLoading(false);
            // Reapply in case metadata load reset it
            audio.playbackRate = playbackRate;
            audio
              .play()
              .then(() => setPlaying(true))
              .catch(() => setPlaying(false));
          };
          audio.onerror = () => {
            if (cancelled) return;
            setLoading(false);
            setStreamError(true);
          };

          audio.load();
        } catch {
          if (cancelled) return;
          setLoading(false);
          setStreamError(true);
        }
      })();

      // Return cleanup for the caller (not strictly needed but handy)
      return () => {
        cancelled = true;
      };
    },
    [activeLecture, muted, volume, playbackRate],
  );

  // ─── Controls ─────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    if (!audioRef.current || loading) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  }, [playing, loading]);

  const skip = useCallback(
    (dir) => {
      if (!audioRef.current) return;
      const newTime = Math.max(
        0,
        Math.min(duration, audioRef.current.currentTime + dir * 15),
      );
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration],
  );

  const seek = useCallback(
    (fraction) => {
      if (!audioRef.current) return;
      const newTime = fraction * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration],
  );

  const setPlaybackRate = useCallback((rate) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
    setPlaybackRateState(rate);
  }, []);

  const closePlayer = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setActiveLecture(null);
    setPlaying(false);
    setCurrentTime(0);
  }, []);

  // Keep volume in sync
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  // Keep playback rate in sync (covers edge cases where audio element is
  // replaced or the browser resets the rate on source change)
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const value = {
    activeLecture,
    playing,
    currentTime,
    duration,
    progress,
    volume,
    muted,
    loading,
    streamError,
    playbackRate,
    playLecture,
    togglePlay,
    skip,
    seek,
    setVolume,
    setMuted,
    setPlaybackRate,
    closePlayer,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}
